import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ErreurApi, api } from '../lib/apiClient';
import type { CategorieDestinataire, FicheDestinataire } from '../types';
import { Chargement, Infobulle, Modale } from './Elements';

/**
 * « Communication groupée » (2026-10-08) — premier temps du socle
 * Messagerie. L'application calcule a qui ecrire ; le message part de
 * Gmail, de la boite de la personne connectee, avec toutes les adresses
 * en CCI : une famille ne doit pas voir les adresses des autres (RGPD).
 *
 * Une ligne par ADRESSE, pas par personne : une mere de deux eleves, un
 * couple qui partage une adresse, ne recoivent qu'un message. Chaque
 * ligne dit pour qui elle vaut et se decoche — c'est ainsi qu'on affine
 * la selection (premiere version a cases par categorie retiree le meme
 * jour, a la demande).
 *
 * Le lot 3 gardera cette modale et remplacera les deux boutons par
 * « Envoyer ».
 */

const QUALITE: Record<CategorieDestinataire, string> = {
  adherent: 'adhérent',
  mere: 'mère',
  pere: 'père',
  tuteur: 'tuteur',
  conjoint: 'conjoint',
  autre: 'contact',
};

/**
 * Au-dela, le lien Gmail devient trop long pour etre ouvert de facon sure
 * — environ 150 a 200 adresses. Copier reste possible.
 */
const LONGUEUR_MAX_LIEN = 7000;

export type SourceDestinataires = { personneIds: string[] } | { creneauId: string };

interface LigneAdresse {
  /** L'email en minuscules : la cle du dedoublonnage. */
  cle: string;
  email: string;
  /** « mère de DUPONT Léa », « DUPONT Paul (adhérent) »… */
  pour: string[];
  /** Les fiches que cette adresse joint. */
  fiches: string[];
}

export function CommunicationGroupee({
  ouverte,
  onFermer,
  source,
  origine,
}: {
  ouverte: boolean;
  onFermer: () => void;
  /** `null` tant que rien n'est demandé. */
  source: SourceDestinataires | null;
  /** Ce qu'on écrit à qui, en clair : « 23 adhérents de la liste », « Piano · lundi 17:00 ». */
  origine: string;
}) {
  const { session } = useAuth();
  const [fiches, setFiches] = useState<FicheDestinataire[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  // Les adresses DECOCHEES : tout part coche, on retire.
  const [ecartees, setEcartees] = useState<Set<string>>(new Set());
  const [copie, setCopie] = useState(false);

  // Le calcul part a chaque ouverture : il est journalise, et la liste a
  // pu changer depuis la derniere fois.
  const cle = source ? JSON.stringify(source) : '';
  useEffect(() => {
    if (!ouverte || !source) return;
    let annule = false;
    setFiches(null);
    setErreur(null);
    setEcartees(new Set());
    setCopie(false);
    api
      .post<{ fiches: FicheDestinataire[] }>('/api/messages/destinataires', source)
      .then((reponse) => {
        if (!annule) setFiches(reponse.fiches);
      })
      .catch((cause) => {
        if (!annule) setErreur(cause instanceof ErreurApi ? cause.message : 'Calcul impossible.');
      });
    return () => {
      annule = true;
    };
    // `cle` porte `source` : un nouvel objet de meme contenu ne relance rien.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouverte, cle]);

  const lignes = useMemo<LigneAdresse[]>(() => {
    const parCle = new Map<string, LigneAdresse>();
    for (const f of fiches ?? []) {
      for (const a of f.adresses) {
        const k = a.email.toLowerCase();
        const ligne = parCle.get(k) ?? { cle: k, email: a.email, pour: [], fiches: [] };
        const qui =
          a.categorie === 'adherent'
            ? `${f.nom} ${f.prenom} (adhérent)`
            : `${QUALITE[a.categorie]} de ${f.nom} ${f.prenom}`;
        if (!ligne.pour.includes(qui)) ligne.pour.push(qui);
        if (!ligne.fiches.includes(f.id)) ligne.fiches.push(f.id);
        parCle.set(k, ligne);
      }
    }
    return [...parCle.values()].sort((a, b) => a.email.localeCompare(b.email));
  }, [fiches]);

  const retenues = lignes.filter((l) => !ecartees.has(l.cle));
  const liste = retenues.map((l) => l.email);
  // Ceux qui ne recevront rien : aucune adresse, ou toutes decochees.
  const joints = new Set(retenues.flatMap((l) => l.fiches));
  const oublies = (fiches ?? []).filter((f) => !joints.has(f.id));

  const basculer = (k: string) => {
    setCopie(false);
    const suite = new Set(ecartees);
    if (suite.has(k)) suite.delete(k);
    else suite.add(k);
    setEcartees(suite);
  };
  const toutes = ecartees.size === 0;

  const moi = session?.utilisateur.email ?? '';
  // Le message s'adresse a soi-meme, les familles en CCI : Gmail a besoin
  // d'un destinataire visible, et chacun garde ainsi sa copie.
  const lienGmail =
    'https://mail.google.com/mail/?' +
    new URLSearchParams({ authuser: moi, view: 'cm', fs: '1', to: moi, bcc: liste.join(',') }).toString();
  const lienTropLong = lienGmail.length > LONGUEUR_MAX_LIEN;

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(liste.join(', '));
      setCopie(true);
    } catch {
      window.prompt('Copiez les adresses (Ctrl+C) :', liste.join(', '));
    }
  };

  return (
    <Modale titre="Communication groupée" ouverte={ouverte} onFermer={onFermer} large>
      <div className="space-y-4 text-sm">
        <p className="text-encre-600">{origine}</p>

        {erreur && (
          <p className="rounded-md border border-red-200 bg-red-50 p-3 text-red-800" role="alert">
            {erreur}
          </p>
        )}
        {!fiches && !erreur && <Chargement libelle="Calcul des destinataires…" />}

        {fiches &&
          (fiches.length === 0 ? (
            <p className="text-encre-500">Personne à qui écrire.</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <p>
                  <span className="tabulaire font-medium text-encre-800">{liste.length}</span> adresse(s)
                  retenue(s) sur <span className="tabulaire">{lignes.length}</span>, pour{' '}
                  <span className="tabulaire">{fiches.length}</span> adhérent(s).
                </p>
                {lignes.length > 0 && (
                  <button
                    type="button"
                    className="text-xs underline"
                    onClick={() => {
                      setCopie(false);
                      setEcartees(toutes ? new Set(lignes.map((l) => l.cle)) : new Set());
                    }}
                  >
                    {toutes ? 'Tout décocher' : 'Tout cocher'}
                  </button>
                )}
              </div>

              {lignes.length > 0 && (
                <ul className="max-h-80 divide-y divide-encre-100 overflow-y-auto rounded-md border border-encre-200">
                  {lignes.map((l) => (
                    <li key={l.cle}>
                      <label className="flex cursor-pointer items-start gap-2 px-3 py-1.5 hover:bg-encre-50">
                        {/* data-navigation : cocher n'est pas une saisie a proteger a la fermeture. */}
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          data-navigation
                          checked={!ecartees.has(l.cle)}
                          onChange={() => basculer(l.cle)}
                        />
                        <span className={ecartees.has(l.cle) ? 'text-encre-400' : ''}>
                          <span className={ecartees.has(l.cle) ? '' : 'text-encre-800'}>{l.email}</span>
                          <span className="block text-xs text-encre-500">{l.pour.join(' · ')}</span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}

              {oublies.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
                  <p className="font-medium">
                    {oublies.length} adhérent(s) ne recevront rien — aucune adresse, ou toutes décochées :
                  </p>
                  <p className="mt-1 text-xs">
                    {oublies.map((f, i) => (
                      <span key={f.id}>
                        {i > 0 && ', '}
                        <Link to={`/adherents/${f.id}`} className="underline" target="_blank" rel="noreferrer">
                          {f.nom} {f.prenom}
                        </Link>
                      </span>
                    ))}
                  </p>
                </div>
              )}

              <p className="text-xs text-encre-500">
                Les adresses partent en <strong>copie cachée</strong> : aucune famille ne voit les autres. Le
                message est envoyé depuis votre boîte ({moi}) et reste dans vos messages envoyés.
              </p>
            </>
          ))}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
          {copie && <span className="text-xs text-green-700">Adresses copiées — à coller dans le champ Cci.</span>}
          <button type="button" className="bouton-secondaire" onClick={onFermer}>
            Fermer
          </button>
          <button type="button" className="bouton-secondaire" disabled={liste.length === 0} onClick={copier}>
            Copier les adresses
          </button>
          <Infobulle
            texte={lienTropLong ? 'Trop d’adresses pour un lien Gmail : copiez-les et collez-les dans le champ Cci.' : null}
            className="inline-flex"
          >
            <button
              type="button"
              className="bouton-principal disabled:pointer-events-none"
              disabled={liste.length === 0 || lienTropLong}
              onClick={() => window.open(lienGmail, '_blank', 'noopener')}
            >
              Ouvrir dans Gmail
            </button>
          </Infobulle>
        </div>
        {lienTropLong && (
          <p className="text-right text-xs text-amber-700">
            Trop d’adresses pour les passer à Gmail par un lien : copiez-les, puis collez-les dans le champ Cci.
          </p>
        )}
      </div>
    </Modale>
  );
}
